import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { 
  Plus, Edit2, Trash2, Building2, User, Mail, Phone, MapPin, 
  ChevronDown, ChevronRight, Search, Users, DollarSign, FileText 
} from 'lucide-react';

export default function ClientsDialog({ open, onOpenChange, clients, onClientsChange }) {
  const [companies, setCompanies] = useState([]);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Form states
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [companyForm, setCompanyForm] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    default_payment_terms: 'net_30',
    notes: ''
  });
  
  const [contactForm, setContactForm] = useState({
    name: '',
    company_id: '',
    title: '',
    email: '',
    phone: '',
    is_primary: false,
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    default_payment_terms: 'net_30',
    notes: ''
  });

  // Load companies
  useEffect(() => {
    if (open) {
      loadCompanies();
    }
  }, [open]);

  const loadCompanies = async () => {
    try {
      const response = await api.getInvoiceCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  // Group contacts by company
  const getGroupedData = () => {
    const standalone = clients.filter(c => !c.company_id);
    const byCompany = {};
    
    companies.forEach(company => {
      byCompany[company.id] = {
        company,
        contacts: clients.filter(c => c.company_id === company.id)
      };
    });
    
    return { standalone, byCompany };
  };

  const { standalone, byCompany } = getGroupedData();

  // Filter based on search
  const filterItems = (items, isCompany = false) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    
    if (isCompany) {
      return items.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
      );
    }
    
    return items.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.company_name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query)
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Company form handlers
  const resetCompanyForm = () => {
    setCompanyForm({
      name: '', email: '', phone: '', website: '',
      address_street: '', address_city: '', address_state: '', address_zip: '',
      default_payment_terms: 'net_30', notes: ''
    });
    setEditingCompany(null);
    setShowCompanyForm(false);
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      website: company.website || '',
      address_street: company.address_street || '',
      address_city: company.address_city || '',
      address_state: company.address_state || '',
      address_zip: company.address_zip || '',
      default_payment_terms: company.default_payment_terms || 'net_30',
      notes: company.notes || ''
    });
    setShowCompanyForm(true);
  };

  const handleSubmitCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingCompany) {
        await api.updateInvoiceCompany(editingCompany.id, companyForm);
        toast.success('Company updated');
      } else {
        await api.createInvoiceCompany(companyForm);
        toast.success('Company created');
      }
      resetCompanyForm();
      loadCompanies();
      onClientsChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async (company) => {
    // Get contact count for this company
    const companyContacts = clients.filter(c => c.company_id === company.id);
    const contactCount = companyContacts.length;
    
    let confirmMessage = `Delete "${company.name}"?`;
    let cascade = false;
    
    if (contactCount > 0) {
      if (!confirm(`"${company.name}" has ${contactCount} contact(s). Do you want to delete the company AND all its contacts? This cannot be undone.`)) {
        return;
      }
      cascade = true;
    } else {
      if (!confirm(confirmMessage + ' This cannot be undone.')) return;
    }
    
    try {
      await api.deleteInvoiceCompany(company.id, cascade);
      toast.success(cascade ? `Company and ${contactCount} contact(s) deleted` : 'Company deleted');
      loadCompanies();
      onClientsChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  // Contact form handlers
  const resetContactForm = () => {
    setContactForm({
      name: '', company_id: '', title: '', email: '', phone: '',
      is_primary: false, address_street: '', address_city: '', 
      address_state: '', address_zip: '', default_payment_terms: 'net_30', notes: ''
    });
    setEditingContact(null);
    setSelectedCompanyId(null);
    setShowContactForm(false);
  };

  const handleAddContactToCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    setContactForm(prev => ({ ...prev, company_id: companyId }));
    setShowContactForm(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || '',
      company_id: contact.company_id || '',
      title: contact.title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      is_primary: contact.is_primary || false,
      address_street: contact.address_street || '',
      address_city: contact.address_city || '',
      address_state: contact.address_state || '',
      address_zip: contact.address_zip || '',
      default_payment_terms: contact.default_payment_terms || 'net_30',
      notes: contact.notes || ''
    });
    setShowContactForm(true);
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    
    setSaving(true);
    try {
      const data = { ...contactForm };
      if (!data.company_id) delete data.company_id;
      
      if (editingContact) {
        await api.updateInvoiceClient(editingContact.id, data);
        toast.success('Contact updated');
      } else {
        await api.createInvoiceClient(data);
        toast.success('Contact created');
      }
      resetContactForm();
      onClientsChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (contact.invoice_count > 0) {
      toast.error(`Cannot delete - ${contact.invoice_count} invoice(s) linked to this contact`);
      return;
    }
    if (!confirm(`Delete "${contact.name}"? This cannot be undone.`)) return;
    
    try {
      await api.deleteInvoiceClient(contact.id);
      toast.success('Contact deleted');
      onClientsChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const toggleCompanyExpand = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  // Render contact row
  const ContactRow = ({ contact, showCompany = false }) => (
    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-slate-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-white font-medium truncate">{contact.name}</p>
            {contact.is_primary && (
              <Badge className="bg-teal-500/20 text-teal-400 text-xs">Primary</Badge>
            )}
            {contact.title && (
              <span className="text-slate-500 text-xs truncate hidden sm:inline">• {contact.title}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {showCompany && contact.company_name && (
              <span className="truncate">{contact.company_name}</span>
            )}
            <span className="truncate">{contact.email}</span>
            {contact.invoice_count > 0 && (
              <span className="text-slate-500">{contact.invoice_count} invoices</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <Button size="sm" variant="ghost" onClick={() => handleEditContact(contact)} className="h-7 w-7 p-0 text-slate-400 hover:text-white">
          <Edit2 className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => handleDeleteContact(contact)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" disabled={contact.invoice_count > 0}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  // Render company card
  const CompanyCard = ({ company, contacts }) => {
    const isExpanded = expandedCompanies[company.id];
    
    return (
      <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
        <div 
          className="p-4 cursor-pointer hover:bg-slate-800/80 transition-colors"
          onClick={() => toggleCompanyExpand(company.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Building2 className="w-5 h-5 text-teal-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <h3 className="text-white font-semibold truncate">{company.name}</h3>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-slate-400">
                  {company.email && (
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {company.email}</span>
                  )}
                  {company.phone && (
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {company.phone}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1 text-slate-500">
                    <Users className="w-3 h-3" /> {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1 text-slate-500">
                    <FileText className="w-3 h-3" /> {company.invoice_count} invoices
                  </span>
                  <span className="flex items-center gap-1 text-teal-400">
                    <DollarSign className="w-3 h-3" /> {formatCurrency(company.total_billed)} billed
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="ghost" onClick={() => handleEditCompany(company)} className="h-7 w-7 p-0 text-slate-400 hover:text-white">
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleDeleteCompany(company)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-slate-700 p-3 space-y-2 bg-slate-900/50">
            {contacts.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-2">No contacts yet</p>
            ) : (
              contacts.map(contact => (
                <ContactRow key={contact.id} contact={contact} />
              ))
            )}
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleAddContactToCompany(company.id)}
              className="w-full mt-2 border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-teal-500"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Contact
            </Button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">Companies & Contacts</DialogTitle>
          <DialogDescription className="text-slate-400">
            Manage your client companies and their contacts
          </DialogDescription>
        </DialogHeader>
        
        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search companies or contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowCompanyForm(true)} className="bg-teal-600 hover:bg-teal-700">
              <Building2 className="w-4 h-4 mr-1" /> Company
            </Button>
            <Button onClick={() => setShowContactForm(true)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
              <User className="w-4 h-4 mr-1" /> Contact
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-slate-800 border-slate-700 w-full justify-start">
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-700">All</TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-slate-700">Companies ({companies.length})</TabsTrigger>
            <TabsTrigger value="standalone" className="data-[state=active]:bg-slate-700">Standalone ({standalone.length})</TabsTrigger>
          </TabsList>
          
          <div className="flex-1 overflow-y-auto mt-4 pr-2 space-y-3">
            <TabsContent value="all" className="m-0 space-y-3">
              {filterItems(companies, true).map(company => (
                <CompanyCard key={company.id} company={company} contacts={byCompany[company.id]?.contacts || []} />
              ))}
              {filterItems(standalone).length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-2">
                    <div className="h-px bg-slate-700 flex-1" />
                    <span className="text-xs text-slate-500 px-2">Standalone Contacts</span>
                    <div className="h-px bg-slate-700 flex-1" />
                  </div>
                  {filterItems(standalone).map(contact => (
                    <ContactRow key={contact.id} contact={contact} showCompany />
                  ))}
                </>
              )}
              {companies.length === 0 && standalone.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No companies or contacts yet</p>
                  <p className="text-sm mt-1">Create a company or add a standalone contact to get started</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="companies" className="m-0 space-y-3">
              {filterItems(companies, true).map(company => (
                <CompanyCard key={company.id} company={company} contacts={byCompany[company.id]?.contacts || []} />
              ))}
              {companies.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No companies yet</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="standalone" className="m-0 space-y-2">
              {filterItems(standalone).map(contact => (
                <ContactRow key={contact.id} contact={contact} showCompany />
              ))}
              {standalone.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No standalone contacts</p>
                  <p className="text-sm mt-1">Contacts not linked to a company will appear here</p>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
        
        {/* Company Form Dialog */}
        <Dialog open={showCompanyForm} onOpenChange={(open) => { if (!open) resetCompanyForm(); }}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">{editingCompany ? 'Edit Company' : 'New Company'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitCompany} className="space-y-4">
              <div>
                <Label className="text-slate-300">Company Name *</Label>
                <Input value={companyForm.name} onChange={(e) => setCompanyForm(p => ({ ...p, name: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={companyForm.email} onChange={(e) => setCompanyForm(p => ({ ...p, email: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input value={companyForm.phone} onChange={(e) => setCompanyForm(p => ({ ...p, phone: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Website</Label>
                <Input value={companyForm.website} onChange={(e) => setCompanyForm(p => ({ ...p, website: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" placeholder="https://" />
              </div>
              <div>
                <Label className="text-slate-300">Street Address</Label>
                <Input value={companyForm.address_street} onChange={(e) => setCompanyForm(p => ({ ...p, address_street: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-slate-300">City</Label>
                  <Input value={companyForm.address_city} onChange={(e) => setCompanyForm(p => ({ ...p, address_city: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">State</Label>
                  <Input value={companyForm.address_state} onChange={(e) => setCompanyForm(p => ({ ...p, address_state: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">ZIP</Label>
                  <Input value={companyForm.address_zip} onChange={(e) => setCompanyForm(p => ({ ...p, address_zip: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Default Payment Terms</Label>
                <Select value={companyForm.default_payment_terms} onValueChange={(v) => setCompanyForm(p => ({ ...p, default_payment_terms: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                    <SelectItem value="net_15">Net 15</SelectItem>
                    <SelectItem value="net_30">Net 30</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Notes</Label>
                <Textarea value={companyForm.notes} onChange={(e) => setCompanyForm(p => ({ ...p, notes: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={resetCompanyForm} className="flex-1 border-slate-600 text-slate-300">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" disabled={saving}>
                  {saving ? 'Saving...' : (editingCompany ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Contact Form Dialog */}
        <Dialog open={showContactForm} onOpenChange={(open) => { if (!open) resetContactForm(); }}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">{editingContact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitContact} className="space-y-4">
              <div>
                <Label className="text-slate-300">Contact Name *</Label>
                <Input value={contactForm.name} onChange={(e) => setContactForm(p => ({ ...p, name: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Company (Optional)</Label>
                <Select value={contactForm.company_id || "none"} onValueChange={(v) => setContactForm(p => ({ ...p, company_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select company..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none">No Company (Standalone)</SelectItem>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Title/Role</Label>
                  <Input value={contactForm.title} onChange={(e) => setContactForm(p => ({ ...p, title: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" placeholder="e.g. Partner, Paralegal" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
                    <input type="checkbox" checked={contactForm.is_primary} onChange={(e) => setContactForm(p => ({ ...p, is_primary: e.target.checked }))} className="rounded border-slate-600" />
                    Primary Contact
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input type="email" value={contactForm.email} onChange={(e) => setContactForm(p => ({ ...p, email: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input value={contactForm.phone} onChange={(e) => setContactForm(p => ({ ...p, phone: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Street Address</Label>
                <Input value={contactForm.address_street} onChange={(e) => setContactForm(p => ({ ...p, address_street: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" placeholder="Leave blank to use company address" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-slate-300">City</Label>
                  <Input value={contactForm.address_city} onChange={(e) => setContactForm(p => ({ ...p, address_city: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">State</Label>
                  <Input value={contactForm.address_state} onChange={(e) => setContactForm(p => ({ ...p, address_state: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">ZIP</Label>
                  <Input value={contactForm.address_zip} onChange={(e) => setContactForm(p => ({ ...p, address_zip: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Notes</Label>
                <Textarea value={contactForm.notes} onChange={(e) => setContactForm(p => ({ ...p, notes: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={resetContactForm} className="flex-1 border-slate-600 text-slate-300">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" disabled={saving}>
                  {saving ? 'Saving...' : (editingContact ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
